import React from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DataGrid } from "@mui/x-data-grid";
import { tickets } from "./tickets";
import { ballsConfig, lotteryBalls } from "./constants";
import { updateDrawnNumbers, subscribeToGame } from "./gameService";
import lt from "./lt";
import { ACCENT_GRADIENT } from "./theme";

const COLUMNS = [
  {
    field: "id",
    headerName: lt.colPlayerId,
    flex: 1,
    minWidth: 140,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "ticketNumber",
    headerName: lt.colTicketNumber,
    flex: 1,
    minWidth: 140,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "numbersLeft",
    headerName: lt.colNumbersLeft,
    valueGetter: (params) =>
      params?.row.numbers.length > 0
        ? params.row.numbers.length - params.row.drawnNumbers.length
        : 0,
    flex: 1,
    minWidth: 140,
    sortable: false,
    disableColumnMenu: true,
  },
];

const DATAGRID_SX = {
  border: 0,
  borderRadius: 0,
  background: "transparent",
  "& .MuiDataGrid-columnHeaders": {
    background: "rgba(255,255,255,0.04)",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  "& .MuiDataGrid-row:hover": {
    background: "rgba(255,255,255,0.04)",
  },
  "& .MuiDataGrid-footerContainer": {
    background: "rgba(255,255,255,0.03)",
    borderTop: "1px solid rgba(255,255,255,0.06)",
  },
  "& .MuiDataGrid-cell": {
    borderColor: "rgba(255,255,255,0.04)",
  },
};

const DATAGRID_LOCALE = {
  MuiTablePagination: {
    labelRowsPerPage: lt.rowsPerPage,
    labelDisplayedRows: lt.displayedRows,
  },
  noRowsLabel: lt.noRows,
};

function buildPlayers(playerCount, drawnNumbers) {
  return tickets.slice(0, playerCount).map((player) => ({
    ...player,
    drawnNumbers: drawnNumbers.filter((n) => player.numbers.includes(n)),
  }));
}

function Lotto({ gameId, playerCount, initialDrawnNumbers, onBack }) {
  const [players, setPlayers] = React.useState(() =>
    buildPlayers(playerCount, initialDrawnNumbers)
  );
  const [lastDrawnNumber, setLastDrawnNumber] = React.useState(0);
  const [allDrawnNumbers, setAllDrawnNumbers] = React.useState(
    initialDrawnNumbers
  );
  const allDrawnRef = React.useRef(initialDrawnNumbers);

  React.useEffect(() => {
    allDrawnRef.current = allDrawnNumbers;
  }, [allDrawnNumbers]);

  React.useEffect(() => {
    const unsubscribe = subscribeToGame(gameId, (data) => {
      const incoming = data.drawnNumbers || [];
      if (JSON.stringify(incoming) === JSON.stringify(allDrawnRef.current))
        return;
      allDrawnRef.current = incoming;
      setPlayers(buildPlayers(playerCount, incoming));
      setAllDrawnNumbers(incoming);
    });
    return unsubscribe;
  }, [gameId, playerCount]);

  const handleNumberInput = (event) => {
    const raw = event.target.value;
    if (raw === "") { setLastDrawnNumber(0); return; }
    const num = Math.min(75, Math.max(1, Number(raw)));
    setLastDrawnNumber(num);
  };

  const handleSubmitClick = (event) => {
    event.preventDefault();
    if (allDrawnNumbers.includes(lastDrawnNumber)) {
      toast.error(lt.toastAlreadyDrawn(lastDrawnNumber), { autoClose: 10000 });
      return;
    }

    const count = players.filter((p) =>
      p.numbers.includes(lastDrawnNumber)
    ).length;
    count > 0
      ? toast.info(lt.toastInTickets(lastDrawnNumber, count, players.length), {
          autoClose: 10000,
        })
      : toast.info(lt.toastNotInTickets(lastDrawnNumber), { autoClose: 10000 });

    const newDrawnNumbers = [...allDrawnNumbers, lastDrawnNumber];
    allDrawnRef.current = newDrawnNumbers;
    setPlayers((prev) =>
      prev.map((p) =>
        p.numbers.includes(lastDrawnNumber) &&
        !p.drawnNumbers.includes(lastDrawnNumber)
          ? { ...p, drawnNumbers: [...p.drawnNumbers, lastDrawnNumber] }
          : p
      )
    );
    setAllDrawnNumbers(newDrawnNumbers);
    setLastDrawnNumber(0);
    updateDrawnNumbers(gameId, newDrawnNumbers);
    checkForTheWinner();
  };

  const handleUndoClick = (event) => {
    event.preventDefault();
    const numberToUndo = allDrawnNumbers[allDrawnNumbers.length - 1];
    if (!numberToUndo) return;

    const newDrawnNumbers = allDrawnNumbers.slice(0, -1);
    allDrawnRef.current = newDrawnNumbers;
    setPlayers((prev) =>
      prev.map((p) => ({
        ...p,
        drawnNumbers: p.drawnNumbers.filter((n) => n !== numberToUndo),
      }))
    );
    setAllDrawnNumbers(newDrawnNumbers);
    updateDrawnNumbers(gameId, newDrawnNumbers);
  };

  const checkForTheWinner = () => {};

  const resolveBallStyle = (number) =>
    lotteryBalls.find((ball) => number >= ball.min && number <= ball.max).style;

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="center"
      sx={{ minHeight: "100vh", p: { xs: 1, sm: 2 } }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: 700,
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        {/* Top accent bar */}
        <Box sx={{ height: 3, background: ACCENT_GRADIENT }} />

        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2 }}
        >
          <Box>
            <Typography variant="h6">{lt.appTitle}</Typography>
            <Typography variant="body2" color="text.secondary">
              {lt.gameLabel}:{" "}
              <Box
                component="span"
                sx={{ color: "primary.main", fontWeight: 600 }}
              >
                {gameId}
              </Box>
              {" · "}
              {lt.playersLabel(playerCount)}
            </Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={onBack}>
            {lt.btnBack}
          </Button>
        </Box>

        <Divider />

        {/* Input section */}
        <Box sx={{ px: 3, py: 2.5 }}>
          <form onSubmit={handleSubmitClick}>
            <Box display="flex" gap={1.5} alignItems="flex-start">
              <TextField
                sx={{ flex: 1 }}
                id="drawn-number"
                value={lastDrawnNumber ? lastDrawnNumber : ""}
                onChange={handleNumberInput}
                onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                type="number"
                label={lt.drawnNumberLabel}
                inputProps={ballsConfig}
                autoFocus
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!lastDrawnNumber}
                sx={{ height: 56 }}
              >
                {lt.btnSubmit}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleUndoClick}
                disabled={allDrawnNumbers.length < 1}
                sx={{ height: 56 }}
              >
                {lt.btnUndo}
              </Button>
            </Box>
          </form>
        </Box>

        {/* Drawn balls */}
        {allDrawnNumbers.length > 0 && (
          <>
            <Divider />
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {allDrawnNumbers.map((number, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 14,
                    transition: "transform 200ms ease",
                    "&:hover": { transform: "scale(1.15)" },
                    ...resolveBallStyle(number),
                  }}
                >
                  {number}
                </Box>
              ))}
            </Box>
          </>
        )}

        <Divider />

        {/* DataGrid */}
        <Box sx={{ width: "100%" }}>
          <DataGrid
            sx={DATAGRID_SX}
            rows={players.toSorted(
              (a, b) =>
                a.numbers.length -
                a.drawnNumbers.length -
                (b.numbers.length - b.drawnNumbers.length)
            )}
            columns={COLUMNS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            localeText={DATAGRID_LOCALE}
          />
        </Box>
      </Paper>

      <ToastContainer
        position="top-right"
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover={false}
        theme="dark"
      />
    </Box>
  );
}

Lotto.propTypes = {
  gameId: PropTypes.string.isRequired,
  playerCount: PropTypes.number.isRequired,
  initialDrawnNumbers: PropTypes.arrayOf(PropTypes.number).isRequired,
  onBack: PropTypes.func.isRequired,
};

export default Lotto;
